import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";

import { Menu } from "lucide-react";
import { motion } from "framer-motion";

import Sidebar from "../components/Sidebar"; // ✅ Ensure this is correct
import Home from "./home";
import TeamManagment from "./team-managment";
import Reports from "./repots";
import Deals from "./deals";
import CreateUser from "./create-user";
import CreateLeadSource from "./create-lead-source";
import {
  getAlldealforAdmin,
  getAllusers,
  getSourceLead,
  updateItem,
  getListingAppointments,
} from "../utils/firebasefunctions";
import { actionType } from "../context/reducer";
import { toast } from "react-toastify";
import { useStateValue } from "../context/StateProvider";
import DealDetail from "./lead-detail";
import Profile from "./profile";
import TeamManagmentDetail from "./team-managment-detail";
import UserManagment from "./team-manament";
import DealBoard from "./deal-board";
import BuyerLeads from "./buyer-leads";
import MainListingLeads from "../components/listing-leads/main-listing-leads";
import UnassignedLeadsView from "../components/unassigned-leads/UnassignedLeadsView";
import CommissionApprovals from "./commission-approvals";
import { getToken } from "firebase/messaging";
import EnableNotificationsCard from "../components/notifications/EnableNotificationsCard";
import { messaging } from "../firebase/firebaseConfig";
import { isSuperAdminOwner } from "../utils/brokerageSettings";
import { arrayUnion } from "firebase/firestore";
import MentionProfile from "./profile-page";
import ListingRequest from "./listing-request";
import Contact from "./contact-page";
import Settings from "./settings";
import Goals from "./goals";
import Feed from "./feed";
import Notifications from "./notifications";
import OpenHouses from "./open-houses";
import OhCheckin from "./oh-checkin";
import RecentlyDeleted from "./recently-deleted";
import FileRequestsPage from "./file-requests";
import Directory from "./directory";
import StatusSheet from "./status-sheet";
import VendorDirectory from "./vendor-directory";
import TextingCenter from "../components/texting/TextingCenter";
import MainFollowUps from "../components/follow-ups/main-follow-ups";
import { notifyDueFollowUps } from "../utils/followUpNotify";
import CallSheetModal from "../components/call-sheet/CallSheetModal";
import { shouldAutoOpen, weekKeyFor } from "../utils/callSheet";
import {
  ensureCurrentSheet,
  subscribeCurrentSheet,
  stampPopupShown,
} from "../utils/callSheetStore";

function Dashboard() {
  const [{ user, users, deals, steps }, dispatch] = useStateValue();
  const navigate = useNavigate();
  if (!user) {
    navigate("/");
  }

  // Per-route access guard: the super-admin owner always passes; otherwise the
  // user's role must be in the allow-list — else redirect to the dashboard home.
  // Stops privileged pages being reached by typing the URL (not just hiding links).
  const guarded = (allowedRoles, element) =>
    user && (isSuperAdminOwner(user) || allowedRoles.includes(user.role))
      ? element
      : <Navigate to="/dashboard" replace />;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapseBar, setIsCollapseBar] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getCollpase = (value) => {
    setIsCollapseBar(value);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
      setIsMobileMenuOpen(false);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Wait for sign-in before touching Firestore — firing these unauthenticated
  // gets a permission-denied from the rules (the "Missing or insufficient
  // permissions" popup people saw when opening the site logged out).
  useEffect(() => {
    if (!user?.id) return;
    getallusers();
    fetchLeadData();
  }, [user?.id]);

  const followUpNotifyFiredFor = useRef(null);
  useEffect(() => {
    if (!user?.id || followUpNotifyFiredFor.current === user.id) return;
    if (!deals?.length) return; // wait for the first deals payload
    followUpNotifyFiredFor.current = user.id;
    notifyDueFollowUps(user, deals);
  }, [user?.id, deals]);

  // Tuesday Call Sheet (owner only): build this week's sheet on first load,
  // pop it up the first time it is ever seen that week, and let the header
  // chip reopen it via the "tes:openCallSheet" event. Mirrors the
  // followUpNotifyFiredFor latch above. Never let failures affect app load.
  const [callSheet, setCallSheet] = useState(null);
  const [showCallSheet, setShowCallSheet] = useState(false);
  const callSheetFiredFor = useRef(null);

  // weekKey state: subscribeCurrentSheet fixes its doc at subscribe time, so an
  // app left open across a Monday-to-Tuesday rollover (installed PWA) must
  // detect the new week and resubscribe + rebuild. Checked when the tab
  // regains visibility and on a 30-minute interval.
  const [callSheetWeekKey, setCallSheetWeekKey] = useState(() =>
    weekKeyFor(new Date())
  );

  useEffect(() => {
    const recheck = () => {
      const nowKey = weekKeyFor(new Date());
      setCallSheetWeekKey((prev) => (prev === nowKey ? prev : nowKey));
    };
    document.addEventListener("visibilitychange", recheck);
    const timer = setInterval(recheck, 30 * 60 * 1000);
    return () => {
      document.removeEventListener("visibilitychange", recheck);
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    // New week while mounted: allow the build/auto-popup latch to fire again.
    callSheetFiredFor.current = null;
  }, [callSheetWeekKey]);

  useEffect(() => {
    if (!user?.id || callSheetFiredFor.current === user.id) return;
    if (!isSuperAdminOwner(user)) return;
    if (!deals?.length) return; // wait for the first deals payload
    callSheetFiredFor.current = user.id;
    (async () => {
      try {
        const { sheet } = await ensureCurrentSheet(deals, user.email, new Date(), user.id);
        if (shouldAutoOpen(sheet)) {
          setCallSheet(sheet);
          setShowCallSheet(true);
          stampPopupShown(sheet.weekOf).catch(() => {});
        }
      } catch (err) {
        // Call-sheet failures must never break the dashboard.
      }
    })();
  }, [user?.id, deals, callSheetWeekKey]);

  useEffect(() => {
    if (!user?.id || !isSuperAdminOwner(user)) return undefined;
    const unsub = subscribeCurrentSheet(setCallSheet);
    const open = () => setShowCallSheet(true);
    window.addEventListener("tes:openCallSheet", open);
    return () => {
      unsub();
      window.removeEventListener("tes:openCallSheet", open);
    };
  }, [user?.id, callSheetWeekKey]);

    useEffect(() => {
    if (user?.id) {
      if (user?.id != undefined) {
        requestPermission();
      }
   
    }
  }, [user]);

    async function requestPermission() {
    // Dev-only: skip push-notification (FCM) setup when running locally. getToken
    // uses the LIVE project's web-push/VAPID credentials, which the sandbox project
    // doesn't have — it throws "messaging/token-subscribe-failed" as an uncaught
    // runtime error. Production builds keep push notifications enabled.
    if (process.env.NODE_ENV === "development") return;
    //requesting permission using Notification API
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      const token = await getToken(messaging, {
        vapidKey:
          "BKNonFk-lAUXh-_LSJ6F1aoG1ZfVupq3qc3FsR2WgQw48IpnqLWRQ5MrYDqFzG4xNfAxovO6AB51e5lhp1JDG3Y",
      });

      //We can send token to server
      console.log("Token generated : ", token);
      if (
        user?.NotificationToken == undefined ||
        user?.NotificationToken?.find((data) => data == token) == undefined
      ) {
        updatenotificationtoken(token);
      }
    }
    // No blocking alert on denial: in a Safari tab on iPhone the request
    // silently no-ops (push needs an installed PWA), so a denial here is
    // expected. The EnableNotificationsCard guides users to the right path.
  }

   const updatenotificationtoken = async (token) => {
    let data = {
      NotificationToken: arrayUnion(token),
    };
    await updateItem("users", user?.id, data)
      .then(async (response) => {})
      .catch((err) => {
        console.error("Failed to save notification token:", err);
      });
  };

  async function getallusers() {
    await getAllusers("users")
      .then(async (response) => {
        dispatch({
          type: actionType.STORE_ALLUSERS,
          teamleads: response.filter(function (doc) {
            return doc.role == "Admin";
          }),
          clients: response.filter(function (doc) {
            return doc.role == "Supervisor";
          }),
          employees: response.filter(function (doc) {
            return doc.role == "Employee" || doc.role == "Admin";
          }),
          allusers: response,
        });
      })
      .catch((err) => {
        console.error("Failed to load users:", err);
      });
  }
  const fetchLeadData = async () => {
    const data = await getSourceLead();
    dispatch({
      type: actionType.STORE_LEADS,
      payload: data,
    });
  };

  async function getalldeal() {
    await getAlldealforAdmin("deals")
      .then(async (response) => {
        dispatch({
          type: actionType.STORE_DEALS,
          deals: response,
        });
      })
      .catch((err) => {
        console.error("Failed to load deals:", err);
      });
  }

  async function getListingAppts() {
    try {
      const items = await getListingAppointments();
      dispatch({
        type: actionType.STORE_LISTING_APPOINTMENTS,
        payload: items,
      });
    } catch (err) {
      console.error("Failed to load listing appointments:", err);
    }
  }

  useEffect(() => {
    if (!user?.id) return;
    getalldeal();
    getListingAppts();
  }, [user?.id]);
  return (
    <div className="flex lg:flex-row flex-col w-full">
      <div className={`${isCollapseBar ? "lg:w-[5.5%]" : "lg:w-[20%]"}`}>
        <button
          className={`fixed top-3 left-4 z-50 ${
            isMobileMenuOpen ? "hidden" : ""
          } lg:hidden bg-gray-300 p-2 rounded-full`}
          onClick={toggleMobileMenu}
        >
          <Menu className="h-6 w-6" />
        </button>
        <Sidebar
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          isCollapse={getCollpase}
        />
      </div>
      <motion.div
        initial={{ x: 0 }}
        animate={isLargeScreen && isCollapseBar ? { x: 0 } : {}}
        transition={{ type: "spring", stiffness: 260, damping: 30 }}
        className={`${
          isCollapseBar ? " w-full" : "lg:w-[80%] w-full"
        }  overflow-y-auto}`}
      >
        <EnableNotificationsCard />
        {showCallSheet && callSheet && (
          <CallSheetModal
            key={`${callSheet.weekOf}:${showCallSheet}`}
            sheet={callSheet}
            user={user}
            users={users || []}
            onClose={() => setShowCallSheet(false)}
          />
        )}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/create-user"
            element={guarded(["Admin", "Supervisor"], <CreateUser />)}
          />
          <Route
            path="/update-user/:id"
            element={guarded(["Admin", "Supervisor"], <CreateUser />)}
          />
          {/* <Route path="/user-management" element={<UserManagment />} /> */}
          <Route
            path="/team-management"
            element={guarded(["Admin", "Accountant"], <TeamManagment />)}
          />
          <Route
            path="/team-management/:id"
            element={guarded(["Admin", "Accountant"], <TeamManagmentDetail />)}
          />
          <Route path="/goals" element={<Goals />} />
          <Route
            path="/reports"
            element={<Navigate to="/dashboard/team-management?tab=reports" replace />}
          />
          <Route path="/deals" element={<Deals />} />
          <Route path="/deals-detail/:id" element={<DealDetail />} />
          {/* <Route path="/create-lead-source" element={<CreateLeadSource />} /> */}
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/:id" element={<MentionProfile />} />
          <Route path="/deal-board" element={<DealBoard />} />
          <Route path="/buyers" element={<BuyerLeads />} />
          <Route path="/listings" element={<MainListingLeads />} />
          <Route
            path="/unassigned-leads"
            element={guarded(["Admin"], <UnassignedLeadsView />)}
          />
          <Route
            path="/commission-approvals"
            element={guarded(["Accountant", "Admin"], <CommissionApprovals />)}
          />
          {/* Task Templates lives in Settings → Task Templates now. */}
          <Route
            path="/task-template"
            element={
              <Navigate to="/dashboard/settings?tab=taskTemplate" replace />
            }
          />
          <Route path="/listing-request" element={<ListingRequest />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/directory" element={<Directory />} />
          {/* Super Admin moved into Settings — keep old links working. */}
          <Route
            path="/super-admin"
            element={
              <Navigate to="/dashboard/settings?tab=superAdmin" replace />
            }
          />
          <Route path="/status-sheet" element={<StatusSheet />} />
          <Route path="/vendors" element={<VendorDirectory />} />
          <Route path="/follow-ups" element={<MainFollowUps />} />
          <Route path="/texting" element={<TextingCenter />} />
          <Route path="/open-houses" element={<OpenHouses />} />
          <Route path="/oh-checkin/:openHouseId" element={<OhCheckin />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/feed" element={<Feed />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/recently-deleted" element={<RecentlyDeleted />} />
          {/* Full File Requests queue — "View all" on the dashboard card. */}
          <Route path="/file-requests" element={<FileRequestsPage />} />
        </Routes>
      </motion.div>
    </div>
  );
}

export default Dashboard;
